import { useState } from "react";
import type { Category } from "../../shared/types";
import { api } from "../lib/api";
import Modal from "./Modal";
import { useConfirm } from "./ConfirmProvider";

const SWATCHES = [
  "#17B886", "#12B76A", "#2E90FA", "#7F56D9", "#F79009",
  "#F04438", "#667085", "#EF6820", "#EE46BC", "#0BA5EC",
];

// Add or edit a category (name + colour), with delete when editing.
export default function CategoryModal({
  category,
  onClose,
  onSaved,
}: {
  category: Category | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = category != null;
  const [name, setName] = useState(category?.name ?? "");
  const [colour, setColour] = useState(category?.colour ?? "#17B886");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const ask = useConfirm();

  async function save() {
    if (!name.trim()) return setError("Give the category a name");
    setSaving(true);
    try {
      if (editing) await api.updateCategory(category.id, name.trim(), colour);
      else await api.createCategory(name.trim(), colour);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
      setSaving(false);
    }
  }

  async function remove() {
    if (!category) return;
    if (!(await ask({ title: "Delete category?", message: `Delete "${category.name}"? Anything using it becomes uncategorised.`, confirmLabel: "Delete", danger: true }))) return;
    setSaving(true);
    try {
      await api.deleteCategory(category.id);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
      setSaving(false);
    }
  }

  return (
    <Modal title={editing ? "Edit category" : "Add category"} onClose={onClose}>
      <div className="form">
        <label className="field">
          <span className="field__label">Name</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Groceries" autoFocus />
        </label>
        <div className="field">
          <span className="field__label">Colour</span>
          <div className="swatches">
            {SWATCHES.map((c) => (
              <button
                key={c}
                type="button"
                className={"swatch" + (colour.toLowerCase() === c.toLowerCase() ? " is-active" : "")}
                style={{ background: c }}
                onClick={() => setColour(c)}
                aria-label={c}
              />
            ))}
            <input type="color" className="swatch swatch--custom" value={colour} onChange={(e) => setColour(e.target.value)} aria-label="Custom colour" />
          </div>
        </div>
        {error && <p className="form__error">{error}</p>}
        <div className="form__actions form__actions--split">
          {editing ? <button className="btn btn--danger" onClick={remove} disabled={saving}>Delete</button> : <span />}
          <div className="form__actions">
            <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn--primary" onClick={save} disabled={saving}>{saving ? "Saving…" : editing ? "Save" : "Add"}</button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
